// Shared "possible duplicate" panel for create-new-household forms
// (New Opportunity's inline household form, New Contact). Renders the
// same live-typing precheck matches HouseholdPage's add-member form
// already shows, plus (new here) a "Use this household" one-click
// action so the operator never has to leave the modal to avoid
// creating a twin.
//
// Distinguishes STRONG matches (same name + same email or phone — the
// combination the server refuses to create a twin for, per
// shared/contacts/duplicate-match.ts) from weaker ones (name+ZIP
// only, or email/phone with a different name — e.g. a spouse sharing
// a household email). Strong matches read as a stop; weak ones read
// as an FYI.

import type { CSSProperties } from "react";
import { ArrowRight } from "lucide-react";

import type { DuplicatePrecheckMatch } from "../api";
import { isStrongDuplicateMatch } from "@shared/contacts/duplicate-match";
import { formatPhone } from "../pages/contact-page-helpers";

function matchSignalLabel(signal: string): string {
  if (signal === "email") return "email";
  if (signal === "phone") return "phone";
  if (signal === "name_zip") return "name+ZIP";
  return signal;
}

const matchActionButtonStyle: CSSProperties = {
  fontSize: 12,
  background: "var(--blue)",
  color: "var(--white)",
  padding: "4px 8px",
  borderRadius: 4,
  border: "none",
  cursor: "pointer",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: 4,
};

export function DuplicateContactWarning({
  matches,
  input,
  onUseHousehold,
  onUseContact,
  onConfirmDifferentPerson,
}: {
  matches: DuplicatePrecheckMatch[];
  /** The name currently typed, used to tell a strong (name+contact)
   *  match apart from a weaker one. */
  input: { legal_first_name?: string | null; legal_last_name?: string | null };
  /** Point the record being created at this EXISTING household instead
   *  of minting a new one — used where nothing new is being created
   *  for the matched person, just attached elsewhere (New Opportunity:
   *  the opportunity targets the existing household; no contact is
   *  created at all). Mutually exclusive with onUseContact. */
  onUseHousehold?: (householdId: string, displayName: string) => void;
  /** Abandon creating a new contact and go straight to the matched
   *  one instead — used where the thing being created IS a contact,
   *  so attaching it to the matched household would still leave a
   *  literal duplicate person record behind (New Contact). Mutually
   *  exclusive with onUseHousehold. */
  onUseContact?: (contactId: string) => void;
  /** When set, a "No, this is someone new" action appears whenever a
   *  strong match is present — the server will otherwise refuse the
   *  create, so this is how the operator gets past a false positive
   *  (two genuinely different people who happen to share a name and
   *  a phone number). */
  onConfirmDifferentPerson?: () => void;
}) {
  if (matches.length === 0) return null;

  const anyStrong = matches.some((m) => isStrongDuplicateMatch(input, m));

  return (
    <div
      role="alert"
      style={{
        background: anyStrong ? "rgba(180, 35, 42, 0.08)" : "rgba(254, 215, 102, 0.18)",
        border: `1px solid ${anyStrong ? "rgba(180, 35, 42, 0.35)" : "rgba(254, 215, 102, 0.6)"}`,
        borderRadius: 6,
        padding: "10px 12px",
        fontSize: 13,
        color: anyStrong ? "var(--danger, #b4232a)" : "var(--warning)",
        marginTop: 10,
      }}
    >
      <strong>
        {anyStrong ? "Already in the OS" : "Possible duplicate"}
        {matches.length === 1 ? "" : "s"} — {matches.length} existing contact
        {matches.length === 1 ? "" : "s"} matched
      </strong>
      <p style={{ margin: "4px 0 8px", fontSize: 12 }}>
        {onUseContact
          ? anyStrong
            ? "This name and email or phone already match someone in the OS. Use that contact, or confirm this is a different person."
            : "These contacts share an email, phone, or name+ZIP with what you're entering. Use one, or ignore and proceed."
          : anyStrong
            ? "This name and email or phone already match someone in the OS. Use their household, or confirm this is a different person."
            : "These contacts share an email, phone, or name+ZIP with what you're entering. Use their household, or ignore and proceed."}
      </p>
      <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
        {matches.map((m) => {
          const fullName = [m.preferred_name ?? m.legal_first_name, m.legal_last_name]
            .filter(Boolean)
            .join(" ");
          return (
            <li
              key={m.contact_id}
              style={{
                padding: "6px 0",
                borderTop: "1px solid rgba(0, 0, 0, 0.1)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong>{fullName || "(no name on file)"}</strong>
                {m.household_display_name && (
                  <span style={{ fontSize: 11, marginLeft: 6, opacity: 0.85 }}>
                    · {m.household_display_name}
                  </span>
                )}
                <div style={{ fontSize: 11, opacity: 0.85 }}>
                  {[m.email, formatPhone(m.phone_primary)].filter(Boolean).join(" · ") || "—"} · matched on{" "}
                  {m.match_signals.map(matchSignalLabel).join(", ")}
                </div>
              </span>
              {onUseContact ? (
                <button
                  type="button"
                  onClick={() => onUseContact(m.contact_id)}
                  style={matchActionButtonStyle}
                >
                  Use this contact
                  <ArrowRight size={14} strokeWidth={1.5} aria-hidden />
                </button>
              ) : (
                onUseHousehold &&
                m.household_id && (
                  <button
                    type="button"
                    onClick={() =>
                      onUseHousehold(
                        m.household_id!,
                        m.household_display_name ?? fullName ?? "Selected household",
                      )
                    }
                    style={matchActionButtonStyle}
                  >
                    Use this household
                    <ArrowRight size={14} strokeWidth={1.5} aria-hidden />
                  </button>
                )
              )}
            </li>
          );
        })}
      </ul>
      {anyStrong && onConfirmDifferentPerson && (
        <button
          type="button"
          onClick={onConfirmDifferentPerson}
          style={{
            marginTop: 8,
            padding: 0,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: 12,
            color: "var(--danger, #b4232a)",
            textDecoration: "underline",
          }}
        >
          No, this is someone new — create anyway
        </button>
      )}
    </div>
  );
}
